import React, { useState, useEffect } from 'react';
import { ledgerAPI, vendorAPI } from '../services/api';
import { toast } from 'react-toastify';
import CURRENCY from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './AddTransactionModal.css';

/**
 * Dedicated Vendor Transaction Modal
 *
 * ENTERPRISE RULE: Vendor payments have a separate, focused flow
 * - Category is fixed to "Vendor Payment" (read-only)
 * - Only vendor-related transaction types
 * - Clear separation between real cash transactions and obligations
 */

// Vendor-specific transaction types
const VENDOR_ENTRY_TYPES = [
  { value: 'expense', label: 'Expense (Paid)', labelHi: 'Kharcha kiya', icon: '−', color: '#DC3545', needsLedger: true },
  { value: 'income', label: 'Income (Received)', labelHi: 'Payment aaya', icon: '+', color: '#28A745', needsLedger: true },
  { value: 'payable', label: 'Payable (Dues)', labelHi: 'Dena hai (Udhar)', icon: '◯', color: '#F39C12', needsLedger: false },
  { value: 'receivable', label: 'Receivable (Owed)', labelHi: 'Udhar lena hai', icon: '◎', color: '#3498DB', needsLedger: false },
];

const VendorTransactionModal = ({ isOpen, onClose, onSuccess, vendors = [], preSelectedVendorId = null }) => {
  const { t, toneEnabled } = useTone();
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [vendorDetails, setVendorDetails] = useState(null); // For overpayment detection
  const [formData, setFormData] = useState({
    entry_type: 'expense',
    vendor_id: preSelectedVendorId || '',
    ledger_id: '',
    amount: '',
    description: '',
    entry_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (isOpen) {
      fetchLedgers();
      // Reset form when modal opens, keeping preSelectedVendorId if provided
      setFormData(prev => ({
        ...prev,
        entry_type: 'expense',
        vendor_id: preSelectedVendorId || '',
        amount: '',
        description: '',
        entry_date: new Date().toISOString().split('T')[0],
      }));
      setVendorDetails(null);
    }
  }, [isOpen, preSelectedVendorId]);

  // Fetch vendor details for overpayment detection
  useEffect(() => {
    const fetchVendorDetails = async () => {
      if (formData.vendor_id) {
        try {
          const response = await vendorAPI.getTransactions(formData.vendor_id);
          setVendorDetails(response.data.data.summary || null);
        } catch (error) {
          // Silently fail - overpayment warning is optional
          setVendorDetails(null);
        }
      } else {
        setVendorDetails(null);
      }
    };
    fetchVendorDetails();
  }, [formData.vendor_id]);

  const fetchLedgers = async () => {
    setLoading(true);
    try {
      const response = await ledgerAPI.getAll();
      const allLedgers = response.data.data.ledgers || [];
      const activeLedgers = allLedgers.filter(l => !l.is_archived);
      setLedgers(activeLedgers);

      // Auto-select first active ledger
      if (activeLedgers.length > 0) {
        setFormData(prev => ({ ...prev, ledger_id: activeLedgers[0].id }));
      }
    } catch (error) {
      toast.error('Failed to load ledgers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const selectedEntryType = VENDOR_ENTRY_TYPES.find(et => et.value === formData.entry_type);
  const needsLedger = selectedEntryType?.needsLedger ?? false;

  // LEDGER BALANCE VALIDATION: Check if expense exceeds available balance
  const selectedLedger = ledgers.find(l => l.id === formData.ledger_id);
  const ledgerBalance = selectedLedger ? parseFloat(selectedLedger.balance || 0) : 0;
  const enteredAmount = parseFloat(formData.amount) || 0;
  const isExpenseType = formData.entry_type === 'expense';
  const insufficientBalance = isExpenseType && enteredAmount > 0 && enteredAmount > ledgerBalance;

  // OVERPAYMENT DETECTION: Check if paying more than pending dues
  const pendingPayable = vendorDetails ? parseFloat(vendorDetails.pending_payable || 0) : 0;
  const pendingReceivable = vendorDetails ? parseFloat(vendorDetails.pending_receivable || 0) : 0;
  const isOverpayingPayable = formData.entry_type === 'expense' && enteredAmount > 0 && enteredAmount > pendingPayable && pendingPayable >= 0;
  const isOverpayingReceivable = formData.entry_type === 'income' && enteredAmount > 0 && enteredAmount > pendingReceivable && pendingReceivable >= 0;
  const overpaymentAmount = isOverpayingPayable ? (enteredAmount - pendingPayable) : isOverpayingReceivable ? (enteredAmount - pendingReceivable) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (needsLedger && !formData.ledger_id) {
      toast.error('Please select a ledger for this transaction type');
      return;
    }

    setSubmitting(true);
    try {
      await vendorAPI.createEntry(formData.vendor_id, {
        entry_type: formData.entry_type,
        amount: parseFloat(formData.amount),
        description: formData.description || undefined,
        // Only send ledger_id for balance-affecting types (expense/income)
        ledger_id: needsLedger ? formData.ledger_id : undefined,
        entry_date: formData.entry_date,
      });

      const typeLabel = selectedEntryType?.label || formData.entry_type;
      toast.success(`${typeLabel} recorded for vendor`);

      // Reset form
      setFormData({
        entry_type: 'expense',
        vendor_id: preSelectedVendorId || '',
        ledger_id: ledgers[0]?.id || '',
        amount: '',
        description: '',
        entry_date: new Date().toISOString().split('T')[0],
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to record vendor transaction');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-transaction-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{toneEnabled ? 'Dukandaar ka Lenden' : 'Vendor Transaction'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="modal-loading">{t('common.loading')}</div>
        ) : (
          <form className="transaction-form" onSubmit={handleSubmit}>
            {/* Entry Type Selection */}
            <div className="form-group">
              <label>{toneEnabled ? 'Lenden ka tarika' : 'Transaction Type'} *</label>
              <div className="transaction-type-buttons" style={{ flexWrap: 'wrap' }}>
                {VENDOR_ENTRY_TYPES.map(et => (
                  <button
                    key={et.value}
                    type="button"
                    className={`type-btn ${formData.entry_type === et.value ? 'active' : ''}`}
                    style={{
                      borderColor: formData.entry_type === et.value ? et.color : undefined,
                      backgroundColor: formData.entry_type === et.value ? et.color : undefined,
                      flex: '1 1 45%',
                      minWidth: '120px',
                    }}
                    onClick={() => handleChange({ target: { name: 'entry_type', value: et.value } })}
                  >
                    <span className="type-icon">{et.icon}</span>
                    {toneEnabled ? et.labelHi : et.label}
                  </button>
                ))}
              </div>
              {!needsLedger && (
                <small style={{ color: '#7F8C8D', fontSize: '0.75rem', marginTop: 8, display: 'block' }}>
                  {toneEnabled
                    ? 'Ye sirf record ke liye hai - paisa nahi hatega'
                    : 'This is an obligation record - no actual cash movement'}
                </small>
              )}
            </div>

            {/* Vendor Selection (Mandatory) */}
            <div className="form-group">
              <label>{t('transactions.vendor')} *</label>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                required
                disabled={!!preSelectedVendorId}
                style={preSelectedVendorId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              >
                <option value="">{toneEnabled ? 'Dukandaar chuno' : 'Select Vendor'}</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>

            {/* Amount */}
            <div className="form-group">
              <label>{t('transactions.amount')} ({CURRENCY.symbol}) *</label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">{CURRENCY.symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  className="amount-input"
                />
              </div>
              {/* OVERPAYMENT WARNING */}
              {(isOverpayingPayable || isOverpayingReceivable) && (
                <div style={{
                  backgroundColor: '#FFF3CD',
                  border: '1px solid #FFECB5',
                  borderRadius: 4,
                  padding: '8px 12px',
                  marginTop: 8,
                  fontSize: '0.85rem',
                  color: '#856404'
                }}>
                  <strong>⚠️ {toneEnabled ? 'Zyada de rahe ho!' : 'Overpayment Warning!'}</strong><br/>
                  {isOverpayingPayable && (
                    <>
                      {toneEnabled
                        ? `Pending: ₹${pendingPayable.toFixed(2)}, Aap de rahe: ₹${enteredAmount.toFixed(2)}`
                        : `Pending payable: ₹${pendingPayable.toFixed(2)}, You are paying: ₹${enteredAmount.toFixed(2)}`}
                      <br/>
                      <span style={{ fontSize: '0.8rem' }}>
                        {toneEnabled
                          ? `₹${overpaymentAmount.toFixed(2)} advance hoga. Proceed karna hai?`
                          : `₹${overpaymentAmount.toFixed(2)} extra will be recorded as advance. Proceed?`}
                      </span>
                    </>
                  )}
                  {isOverpayingReceivable && (
                    <>
                      {toneEnabled
                        ? `Pending: ₹${pendingReceivable.toFixed(2)}, Aap le rahe: ₹${enteredAmount.toFixed(2)}`
                        : `Pending receivable: ₹${pendingReceivable.toFixed(2)}, You are receiving: ₹${enteredAmount.toFixed(2)}`}
                      <br/>
                      <span style={{ fontSize: '0.8rem' }}>
                        {toneEnabled
                          ? `₹${overpaymentAmount.toFixed(2)} advance aayega. Proceed karna hai?`
                          : `₹${overpaymentAmount.toFixed(2)} extra will be recorded as advance. Proceed?`}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Ledger (only for expense/income) */}
            {needsLedger && (
              <div className="form-group">
                <label>{t('transactions.ledger')} *</label>
                <select name="ledger_id" value={formData.ledger_id} onChange={handleChange} required>
                  <option value="">{toneEnabled ? 'Khata chuno' : 'Select Ledger'}</option>
                  {ledgers.map(l => (
                    <option key={l.id} value={l.id}>{l.name} (₹{parseFloat(l.balance || 0).toFixed(2)})</option>
                  ))}
                </select>
                <small style={{ color: '#7F8C8D', fontSize: '0.75rem' }}>
                  {formData.entry_type === 'expense'
                    ? (toneEnabled ? 'Paisa is khate se jayega' : 'Money will be deducted from this ledger')
                    : (toneEnabled ? 'Paisa is khate mein aayega' : 'Money will be added to this ledger')}
                </small>
                {/* LEDGER BALANCE WARNING: Show when expense exceeds balance */}
                {insufficientBalance && (
                  <div style={{
                    backgroundColor: '#FFF3CD',
                    border: '1px solid #FFECB5',
                    borderRadius: 4,
                    padding: '8px 12px',
                    marginTop: 8,
                    fontSize: '0.85rem',
                    color: '#856404'
                  }}>
                    <strong>⚠️ {toneEnabled ? 'Balance kam hai' : 'Insufficient balance'}.</strong><br/>
                    {toneEnabled ? 'Available' : 'Available'}: ₹{ledgerBalance.toFixed(2)}, {toneEnabled ? 'Chahiye' : 'Required'}: ₹{enteredAmount.toFixed(2)}<br/>
                    <span style={{ fontSize: '0.8rem' }}>{toneEnabled ? 'Payable (Dena hai) use karo - paisa nahi hatega' : 'Please use Payable (Dues) instead - no cash movement.'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div className="form-group">
              <label>{t('transactions.description')}</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={toneEnabled ? 'Kya hai ye lenden?' : 'What is this transaction for?'}
              />
            </div>

            {/* Date */}
            <div className="form-group">
              <label>{t('transactions.date')}</label>
              <input
                type="date"
                name="entry_date"
                value={formData.entry_date}
                onChange={handleChange}
              />
            </div>

            {/* Category Info (Read-only) */}
            <div className="form-group">
              <label>{t('transactions.category')}</label>
              <input
                type="text"
                value="Vendor Payment"
                disabled
                style={{ backgroundColor: '#f5f5f5', color: '#7F8C8D', cursor: 'not-allowed' }}
              />
              <small style={{ color: '#7F8C8D', fontSize: '0.75rem' }}>
                {toneEnabled ? 'Category fix hai vendor lenden ke liye' : 'Category is fixed for vendor transactions'}
              </small>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                {t('action.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? (toneEnabled ? 'Bacha rahe hai...' : 'Saving...') : (toneEnabled ? 'Lenden bachao' : 'Save Transaction')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default VendorTransactionModal;
